import React, { useState } from 'react';
import {
  User, Building, Building2, Users, Landmark, Factory,
  Home, Briefcase, Shield, CheckCircle, PieChart, FileText,
  CheckSquare, Square
} from 'lucide-react';
import { Badge } from '../ui/badge';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Coût en crédits pour une simulation DOMIUM
export const DOMIUM_SIMULATION_CREDIT_COST = 25;

// Helper pour extraire un message d'erreur lisible depuis une réponse API
export const getErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMessage;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(err => err.msg || err.message || String(err)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || detail.error || defaultMessage;
  }
  return defaultMessage;
};

// Charte graphique ESS COPPAM
export const ESS_COLORS = {
  vertPrincipal: '#0A7F5A',
  bleuInstitutionnel: '#0B3C5D',
  sableClair: '#F4F6F3',
  grisStructurant: '#4B5563',
};

// Configuration des qualités de souscripteur
export const QUALITES = [
  { value: 'particulier', label: 'Particulier', icon: User, description: 'Personne physique' },
  { value: 'sci', label: 'SCI', icon: Building, description: 'Société Civile Immobilière' },
  { value: 'promoteur', label: 'Promoteur', icon: Building2, description: 'Promoteur immobilier' },
  { value: 'bailleur_social', label: 'Bailleur social', icon: Users, description: 'Organisme HLM, OPH' },
  { value: 'collectivite', label: 'Collectivité', icon: Landmark, description: 'Commune, Département, Région' },
  { value: 'entreprise', label: 'Entreprise', icon: Factory, description: 'Société commerciale' },
  { value: 'cooperative', label: 'Coopérative', icon: Users, description: "Coopérative d'habitation" },
];

// Types d'opération
export const TYPES_OPERATION = [
  { value: 'construction_neuve', label: 'Construction neuve' },
  { value: 'extension', label: 'Extension' },
  { value: 'renovation_lourde', label: 'Rénovation lourde' },
  { value: 'rehabilitation', label: 'Réhabilitation' },
  { value: 'surelevation', label: 'Surélévation' },
];

// Types de bâtiment
export const TYPES_BATIMENT = [
  { value: 'individuel', label: 'Individuel', icon: Home },
  { value: 'collectif', label: 'Collectif', icon: Building },
  { value: 'mixte', label: 'Mixte', icon: Building2 },
  { value: 'tertiaire', label: 'Tertiaire', icon: Briefcase },
];

// Territoires DOM
export const TERRITOIRES = [
  { value: 'guadeloupe', label: 'Guadeloupe', emoji: '🌴', code: '971' },
  { value: 'martinique', label: 'Martinique', emoji: '🌺', code: '972' },
  { value: 'guyane', label: 'Guyane', emoji: '🌳', code: '973' },
  { value: 'reunion', label: 'La Réunion', emoji: '🌋', code: '974' },
  { value: 'mayotte', label: 'Mayotte', emoji: '🏝️', code: '976' },
  { value: 'saint_martin', label: 'Saint-Martin', emoji: '🏖️', code: '978' },
  { value: 'saint_barthelemy', label: 'Saint-Barthélemy', emoji: '⛵', code: '977' },
];

// Composant affectation contribution
export const ContributionAllocation = ({ affectation }) => {
  const items = [
    { label: 'Fonds de garantie', value: affectation?.fonds_garantie || 0, percent: 60, color: 'bg-emerald-500', icon: Shield },
    { label: 'Prévention & expertise', value: affectation?.prevention_expertise || 0, percent: 25, color: 'bg-blue-500', icon: CheckCircle },
    { label: 'Gestion solidaire', value: affectation?.gestion_solidaire || 0, percent: 15, color: 'bg-purple-500', icon: Users }
  ];
  
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
        <PieChart className="h-4 w-4" />
        Affectation de la contribution
      </h4>
      <div className="grid grid-cols-3 gap-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
            <div className={`w-10 h-10 rounded-full ${item.color} mx-auto mb-2 flex items-center justify-center`}>
              <item.icon className="h-5 w-5 text-white" />
            </div>
            <div className="text-xl font-bold text-gray-800">{item.percent}%</div>
            <div className="text-sm font-semibold text-gray-700">{item.value?.toLocaleString('fr-FR')} €</div>
            <div className="text-xs text-gray-500 mt-1">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant checklist des documents
export const DocumentsChecklist = ({ documents }) => {
  const [checked, setChecked] = useState({});
  
  const toggleDoc = (code) => {
    setChecked(prev => ({ ...prev, [code]: !prev[code] }));
  };
  
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
        <FileText className="h-4 w-4" />
        Actions requises
      </h4>
      <div className="space-y-2">
        {documents?.map((doc, idx) => (
          <div 
            key={idx} 
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              checked[doc.code] ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => toggleDoc(doc.code)}
            data-testid={`doc-${doc.code}`}
          >
            {checked[doc.code] ? (
              <CheckSquare className="h-5 w-5 text-emerald-600" />
            ) : (
              <Square className="h-5 w-5 text-gray-400" />
            )}
            <div className="flex-1">
              <span className="font-medium text-gray-700">{doc.label}</span>
              {doc.obligatoire && (
                <Badge variant="outline" className="ml-2 text-xs text-red-600 border-red-300">Obligatoire</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
